'use client';

import { useEffect } from 'react';
import { useAuthStore } from '@/stores/user.store';
import { useThemeStore } from '@/stores/theme.store';
import { useParkingValidationStore } from '@/stores/parking-validation.store';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { isParkingValidationVisible } from '@/lib/parking-validation-utils';

/**
 * Loads the signed-in user's parking-validation events once per login and
 * returns whether the Parking Validation nav item should be shown.
 *
 * The visibility rule lives in {@link isParkingValidationVisible}. Events are a
 * required input to that rule, so this hook triggers the fetch at layout mount
 * (mirroring the legacy reservation-site header, which fetches events on mount).
 * The fetch re-runs whenever the signed-in user changes so events never carry
 * over from a previous session; the store dedupes concurrent callers.
 *
 * @returns true when the Parking Validation nav item should be visible
 */
export function useParkingValidationVisible(): boolean {
  const user = useAuthStore((s) => s.user);
  const theme = useThemeStore((s) => s.theme);
  const companyThemes = useThemeStore((s) => s.companyThemes);
  const events = useParkingValidationStore((s) => s.events);
  const fetchEvents = useParkingValidationStore((s) => s.fetchEvents);
  const resetEventsForUser = useParkingValidationStore((s) => s.resetEventsForUser);
  const { getRecaptchaHeader } = useRecaptcha();

  const userId = user?.userId;

  useEffect(() => {
    if (!userId) return;
    // A different user (re-login, impersonation) must not inherit the last user's events.
    if (useParkingValidationStore.getState().eventsUserId !== userId) {
      resetEventsForUser(userId);
    }
    let cancelled = false;
    const loadEvents = async () => {
      const options = await getRecaptchaHeader('GET_EVENTS');
      if (!cancelled) await fetchEvents(options);
    };
    loadEvents();
    return () => {
      cancelled = true;
    };
    // Re-fetch only when the signed-in user changes; fetchEvents is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  return isParkingValidationVisible(user, theme, companyThemes, events);
}
